import { JQueryInput } from "./types";

let clickInProgress = false;

/** 
 * Indicate whether a click is currently in progress.
 * When a click is in progress, buttons will be visually disabled, and manageButtonClick-wrapped functions
 * will return immediately without calling their inner function.
 */
export function isClickInProgress()
{
    return clickInProgress;
}

/** 
 * Resolve the passed-in deferred when the promise returned by `func` resolves.
 * Consider using Promise.any() instead.
 */
export function waitForFunction(deferred: JQueryDeferred<any>, func: (() => any) | JQueryPromise<any> | Promise<void>)
{
    const promise: JQueryPromise<any> = $.when(func);
    promise.done(function () { deferred.resolve(); });
    promise.fail(function () { deferred.reject(); });
    return promise;
}

/** 
 * Wraps a function with promise-based debouncing.  When called multiple times, subsequent calls
 * will return the last unresolved promise.
 */
export function manageFunction<TFunc extends (...args: any[]) => Promise<any>>(func: TFunc): TFunc
{
    let currentPromise: Promise<Awaited<ReturnType<TFunc>>> | null = null;
    return function (this: ThisParameterType<TFunc>)
    {
        if (!currentPromise)
        {
            currentPromise = (async () =>
            {
                try
                {
                    return await func.apply(this, arguments as unknown as Parameters<TFunc>);
                }
                finally
                {
                    currentPromise = null;
                }
            })();
        }
        return currentPromise!;
    } as unknown as TFunc;
}

/** 
 * Wraps a button's click function so that only one button can be clicked at a time.
 * isClickInProgress will be set to true when the wrapper is called, until the promise returned by `click`
 * resolves.
*/
export function manageButtonClick<TClick extends (this: HTMLElement, ...args: any) => any>(clickFunction: TClick): (this: HTMLElement, ...args: Parameters<TClick>) => PromiseLike<any>
{
    const wrapper = function (this: any)
    {
        // We need to pass "this" and "arguments" along to manageInlineButtonClick, so that we are able to call .apply() with the correct values.
        return manageInlineButtonClick(clickFunction, this, arguments as unknown as any[]);
    };
    wrapper.force = function (thisArg: HTMLElement): ReturnType<typeof manageInlineButtonClick>
    {
        return manageInlineButtonClick(clickFunction, thisArg, arguments as unknown as any[], true);
    };
    return wrapper;
}

/** 
 * Manually set whether a click is in progress.
 * When a click is in progress, buttons will be visually disabled, and manageButtonClick-wrapped functions
 * will return immediately without calling their inner function.
 */
export function setClickInProgress(value: boolean)
{
    clickInProgress = value;
    setClass("#moduleContent a.button, #detailsPanel a.button, .workflowPopup a.button, .messageBox a.button", clickInProgress);
}

export function setClass(selector: JQueryInput, isClickInProgress: boolean)
{
    $(selector).toggleClass("frmwrk-disabled", isClickInProgress);
}

export function isElementDisabled(element: JQuery<HTMLElement>)
{
    return element.hasClass('ui-disabled') || element.hasClass('frmwrk-disabled');
}

async function manageInlineButtonClick(clickFunction: (...args: any[]) => any, currentElement: JQueryInput, parameters: any[], force = false)
{
    const jqElement = $(currentElement);
    if (isElementDisabled(jqElement))
    {
        return;
    }

    const cleanup = () => setClickInProgress(false);

    if (!isClickInProgress())
    {
        setClickInProgress(true);

        try
        {
            const result = clickFunction.apply(currentElement, parameters);

            // JQuery runs always() synchronously on already-resolved Deferreds, so we have to handle them specially
            if (typeof result?.always === "function")
            {
                return result.always(cleanup);
            }

            // Only await if we have to
            // When callers pass in a synchronous `clickFunction`, they expect the button state to be restored before returning
            const awaitedResult = typeof result?.then === "function" ? await result : result;
            return awaitedResult;
        }
        finally
        {
            cleanup();
        }
    }
    else if (force)
    {
        // force doesn't touch clickInProgress.
        return Promise.resolve(clickFunction.apply(currentElement, parameters));
    }
    else
    {
        return Promise.reject(new Error("Click already in progress"));
    }
}
